import React, { useEffect, useRef } from "react";
import styles from "../styles/ChatClassification.module.css";

const BAR_COUNT = 24;
const MIN_HEIGHT_PERCENT = 8;
const SMOOTHING = 0.25;

interface Props {
  analyserRef: React.RefObject<AnalyserNode | null>;
}

const AudioWaveform: React.FC<Props> = ({ analyserRef }) => {
  const barRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const smoothedHeightsRef = useRef<number[]>(new Array(BAR_COUNT).fill(MIN_HEIGHT_PERCENT));

  useEffect(() => {
    let frameId: number;
    let dataArray: Uint8Array<ArrayBuffer> | null = null;

    const tick = () => {
      const analyser = analyserRef.current;

      if (analyser) {
        if (!dataArray || dataArray.length !== analyser.frequencyBinCount) {
          dataArray = new Uint8Array(analyser.frequencyBinCount);
        }
        analyser.getByteFrequencyData(dataArray);

        const groupSize = Math.max(1, Math.floor(dataArray.length / BAR_COUNT));
        for (let i = 0; i < BAR_COUNT; i++) {
          const start = i * groupSize;
          const end = i === BAR_COUNT - 1 ? dataArray.length : start + groupSize;

          let sum = 0;
          for (let j = start; j < end; j++) sum += dataArray[j];
          const average = end > start ? sum / (end - start) : 0;

          // sqrt curve makes quiet room noise visible without amplifying it into randomness
          const normalized = Math.sqrt(average / 255) * 100;
          const target = Math.max(MIN_HEIGHT_PERCENT, Math.min(100, normalized));

          const previous = smoothedHeightsRef.current[i];
          const next = previous + (target - previous) * SMOOTHING;
          smoothedHeightsRef.current[i] = next;

          const bar = barRefs.current[i];
          if (bar) bar.style.height = `${next}%`;
        }
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [analyserRef]);

  return (
    <div className={styles.audioWaveform} aria-hidden="true">
      {Array.from({ length: BAR_COUNT }).map((_, index) => (
        <span
          key={index}
          ref={(el) => {
            barRefs.current[index] = el;
          }}
          className={styles.audioWaveformBar}
        />
      ))}
    </div>
  );
};

export default AudioWaveform;
